import type { Filter, RequestState } from "@elastic/search-ui";

const fieldToParameter: Record<string, string> = {
  "ItemBrand.value.keyword": "brand",
  "ItemSubBrandList.value.keyword": "subBrand",
  "ItemProductCategory.value.keyword": "category",
  "ItemProductGroup.value.keyword": "group",
  "ItemProductSubGroup.value.keyword": "subGroup",
  "ItemLocalUSP.value.keyword": "localUsp",
  "ItemGeneralUSP.value.keyword": "globalUsp",
  ItemIsPrivateLabel: "privateLabel",
  "ItemSoldState.value.keyword": "preservation",
  "ItemTemperatureCondition.value.keyword": "temperature",
  "ItemProductPreparation.value.keyword": "preparation",
  "ItemCommercialUnit.value.keyword": "commercialUnit",
  "ItemLUOwner.value.keyword": "luOwner",
  ItemNetWeight: "weight",
  "packages.PackageUnitDescriptor.value.keyword": "package",
};
const parameterToField = Object.fromEntries(Object.entries(fieldToParameter).map(([key, value]) => [value, key]));

export const withoutObsoleteFilters = (filters: Filter[]): Filter[] => filters.filter((filter) => filter.field !== "ItemSliced.key");

export function stateToUrl(state: RequestState, view = "list", language = "en"): string {
  const params = new URLSearchParams();
  if (state.searchTerm) params.set("q", state.searchTerm);
  for (const filter of withoutObsoleteFilters(state.filters ?? [])) {
    const parameter = fieldToParameter[filter.field] ?? filter.field;
    for (const value of filter.values) {
      if (typeof value === "object" && !Array.isArray(value)) params.append(parameter, `${value.from ?? ""}..${value.to ?? ""}`);
      else params.append(parameter, String(value));
    }
  }
  if (state.sortField) params.set("sort", `${state.sortField}:${state.sortDirection ?? "asc"}`);
  if (view !== "list") params.set("view", view);
  if (language !== "en") params.set("language", language);
  params.set("page", String(state.current ?? 1));
  params.set("pageSize", String(state.resultsPerPage ?? 20));
  return `/product-management/search?${params.toString()}`;
}

export function urlToState(url: string): RequestState {
  const params = new URL(url, window.location.origin).searchParams;
  const filters: Filter[] = [];
  for (const [parameter, field] of Object.entries(parameterToField)) {
    const rawValues = params.getAll(parameter);
    if (!rawValues.length) continue;
    const values = rawValues.map((value) => {
      if (field === "ItemNetWeight" && value.includes("..")) {
        const [from, to] = value.split("..");
        return { from: from ? Number(from) : undefined, to: to ? Number(to) : undefined, name: `${from || "0"}–${to || "∞"} g` };
      }
      if (field === "ItemIsPrivateLabel") return value === "true";
      return value;
    });
    filters.push({ field, type: "any", values: values as Filter["values"] });
  }
  const [sortField, sortDirection] = (params.get("sort") ?? "").split(":");
  return {
    searchTerm: params.get("q") ?? "",
    filters,
    current: Math.max(1, Number(params.get("page")) || 1),
    resultsPerPage: [20, 50, 100].includes(Number(params.get("pageSize"))) ? Number(params.get("pageSize")) : 20,
    ...(sortField ? { sortField, sortDirection: sortDirection === "desc" ? "desc" : "asc" } : {}),
  };
}

export const getViewFromUrl = () => new URLSearchParams(window.location.search).get("view") === "grid" ? "grid" : "list";
export const getLanguageFromUrl = () => ["en", "nl", "fr", "de"].includes(new URLSearchParams(window.location.search).get("language") ?? "")
  ? new URLSearchParams(window.location.search).get("language")! : "en";
